import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiUser, FiLock, FiBriefcase, FiMail, FiEye, FiEyeOff, FiArrowRight, FiCheck } from 'react-icons/fi';
import { register } from '../services/authService';

const Register = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (error) setError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateStep1 = () => {
    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const prevStep = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateStep2()) {
      return;
    }
    
    setLoading(true);

    try {
      const response = await register(formData);
      
      if (response.requiresVerification) {
        // Show success message and redirect to login
        setError('');
        alert('Registration successful! Please check your email to verify your account.');
        navigate('/login');
      } else {
        // Redirect based on user role (fallback for existing users)
        const { role } = response.user;
        if (role === 'admin') {
          navigate('/admin/dashboard');
        } else if (role === 'manager') {
          navigate('/manager/dashboard');
        } else {
          navigate('/employee/dashboard');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Maintenance message */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-bold text-primary-600">TaskFlowPro</h1>
            <h2 className="mt-6 text-center text-2xl font-medium text-gray-900">
              Registration Temporarily Unavailable
            </h2>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Service Maintenance
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Registration is currently down for maintenance. Please try again later or contact support if you need immediate access.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      {/* Right side - Benefits */}
      <div className="hidden md:block md:w-1/2 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="flex flex-col justify-center items-center h-full px-8 text-white">
          <div className="max-w-md">
            <h2 className="text-3xl font-bold mb-6">Join thousands of teams using TaskFlowPro</h2>
            <p className="text-lg mb-8">
              Create your account and start managing your tasks more efficiently today.
            </p>
            <ul className="space-y-4">
              <li className="flex items-center">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-500 flex items-center justify-center mr-2">
                  <FiCheck className="h-4 w-4 text-white" />
                </div>
                <span>Free 14-day trial, no credit card required</span>
              </li>
              <li className="flex items-center">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-500 flex items-center justify-center mr-2">
                  <FiCheck className="h-4 w-4 text-white" />
                </div>
                <span>Unlimited tasks and projects</span>
              </li>
              <li className="flex items-center">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-500 flex items-center justify-center mr-2">
                  <FiCheck className="h-4 w-4 text-white" />
                </div>
                <span>Team collaboration tools</span>
              </li>
              <li className="flex items-center">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-500 flex items-center justify-center mr-2">
                  <FiCheck className="h-4 w-4 text-white" />
                </div>
                <span>Priority customer support</span>
              </li>
            </ul>
            
            <div className="mt-10 p-6 bg-white bg-opacity-10 rounded-lg">
              <p className="italic text-white text-opacity-90">
                "TaskFlowPro has transformed how our team manages projects. We've increased productivity by 35% since implementing it."
              </p>
              <div className="mt-4 flex items-center">
                <div className="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="text-white font-bold">JD</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-white">John Doe</p>
                  <p className="text-xs text-white text-opacity-75">CEO, TechCorp</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;